import { useCallback, useEffect, useRef, useState } from 'react';

interface Page<T> {
  items: T[];
  hasMore: boolean;
}

/** Build a cursor from a row's timestamp + id — matches the server's `parseCursor`. */
export function rowCursor(createdAt: string | number | Date, id: string): string {
  return `${new Date(createdAt).getTime()}_${id}`;
}

/**
 * Accumulating paginated list with scroll-to-load-more, using keyset (cursor)
 * pagination instead of offsets so the list stays stable when rows are inserted
 * or removed between page loads.
 *
 * `fetchPage(cursor)` returns one page plus whether more rows exist; `cursor` is
 * `null` for the first page and otherwise the value of `getCursor(lastItem)`
 * from the previous page. The list loads page 0 on mount (and whenever
 * `resetKey` changes), appends the next page when `onScroll` fires near the
 * bottom, and stops when `hasMore` is false. Pass `enabled = false` to hold off
 * (e.g. logged-out) — the list clears.
 */
export function useInfiniteList<T>(
  fetchPage: (cursor: string | null) => Promise<Page<T>>,
  getCursor: (item: T) => string,
  resetKey: unknown,
  enabled = true,
) {
  const [items, setItems] = useState<T[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const cursorRef = useRef<string | null>(null);
  const loadingRef = useRef(false);
  const fetchRef = useRef(fetchPage);
  fetchRef.current = fetchPage;
  const cursorFnRef = useRef(getCursor);
  cursorFnRef.current = getCursor;

  const run = useCallback(async (reset: boolean) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    const cursor = reset ? null : cursorRef.current;
    try {
      const { items: page, hasMore: more } = await fetchRef.current(cursor);
      if (page.length > 0) cursorRef.current = cursorFnRef.current(page[page.length - 1]);
      setItems((prev) => (reset ? page : [...prev, ...page]));
      setHasMore(more);
    } catch {
      if (reset) {
        setItems([]);
        setHasMore(false);
      }
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      cursorRef.current = null;
      setItems([]);
      setHasMore(false);
      return;
    }
    cursorRef.current = null;
    run(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey, enabled]);

  const onScroll = useCallback(
    (e: React.UIEvent<HTMLElement>) => {
      const el = e.currentTarget;
      if (hasMore && !loadingRef.current && el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
        run(false);
      }
    },
    [hasMore, run],
  );

  return { items, hasMore, loading, onScroll };
}
